import { Box, Group } from '@mantine/core';
import { SettingsBlock, SettingsButton } from '@repo/common/components';
import { Icon, IconButton, Select, Text } from '@repo/foundations';
import type { ISlackChannel } from '../../../api/types/models/slack';

export type MonitorActionSlackChannel = {
	name: string;
	id: string;
};

export type MonitorActionSlack = {
	channels: MonitorActionSlackChannel[];
};

interface MonitorSlackFormProps {
	value: MonitorActionSlack;
	onChange: (value: MonitorActionSlack) => void;
	isAddingSlackChannel: boolean;
	onSetIsAddingSlackChannel: (isAdding: boolean) => void;
	availableSlackChannels: ISlackChannel[] | undefined;
}

export function MonitorSlackForm({
	value,
	onChange,
	isAddingSlackChannel,
	onSetIsAddingSlackChannel,
	availableSlackChannels,
}: MonitorSlackFormProps) {
	const availableChannels = availableSlackChannels;
	const channelCount = value?.channels?.length || 0;

	// Create options for the dropdown, excluding already selected channels
	const channelOptions = (availableChannels || [])
		.filter(
			(channel) =>
				!value?.channels?.some((selected) => selected.id === channel.id)
		)
		.map((channel) => ({
			label: `#${channel.name}`,
			value: channel.id,
		}));

	const handleAddChannel = (channelId: string | null) => {
		if (!channelId || !availableChannels) return;

		const selectedChannel = availableChannels.find(
			(channel) => channel.id === channelId
		);
		if (!selectedChannel) return;

		const newChannel = {
			name: selectedChannel.name,
			id: selectedChannel.id,
		};

		onChange({
			channels: [...(value?.channels || []), newChannel],
		});
		onSetIsAddingSlackChannel(false);
	};

	const handleRemoveChannel = (channelId: string) => {
		onChange({
			channels: (value?.channels || []).filter(
				(channel) => channel.id !== channelId
			),
		});
	};

	// Don't render if no channels are available
	if (!availableChannels) {
		return null;
	}

	return (
		<Box>
			<Box mb="4xl">
				<SettingsButton
					iconName="plus"
					variant="tertiary"
					title="Slack"
					onClick={() => onSetIsAddingSlackChannel(true)}
					tooltip="Add Slack channel"
					aria-label="Add Slack channel"
					description="Incident details can be sent to Slack channels"
				/>

				{(value?.channels || []).map((channel) => (
					<SettingsBlock
						key={channel.id}
						title={
							<Group noWrap align="center" spacing="xs">
								<Icon name="slack" color="icon/secondary/default" />
								<Text weight="semibold">#{channel.name}</Text>
							</Group>
						}
						description={null}
						rightComponent={
							<IconButton
								iconName="x"
								variant="tertiary"
								tooltip="Remove channel"
								onClick={() => handleRemoveChannel(channel.id)}
							/>
						}
					/>
				))}

				{isAddingSlackChannel && (
					<SettingsBlock
						key="add"
						title={
							<Group>
								<Select
									placeholder="Select a Slack channel"
									data={channelOptions}
									value={null}
									onChange={handleAddChannel}
									searchable
									clearable
									autoFocus
									size="sm"
									style={{ flex: 1 }}
									nothingFound={
										channelOptions.length === 0
											? 'All channels already added'
											: 'No channels found'
									}
								/>
							</Group>
						}
						rightComponent={
							<IconButton
								iconName="x"
								variant="tertiary"
								tooltip="Cancel"
								onClick={() => onSetIsAddingSlackChannel(false)}
							/>
						}
					/>
				)}
			</Box>
		</Box>
	);
}
